import React, { useState } from 'react'
import { useAtom } from 'jotai'
import Classnames from 'classnames'

// helper functions
import { returnValue } from '@/utils/op-types/helperFunctions'

// store
import {
  isBuilder,
  manifestStatic,
  pageContentStatic,
  manifestDesign,
  pageContentDesign,
  manifestBlocks,
  pageContentBlocks,
  headerSize,
} from '@/utils/op-types/store'

// block renderer
import BlockRenderer from '@/components/optypes/frontend/BlockRenderer'

// block builder tools
import StaticContentWrapper from '@/components/optypes/frontend/StaticContentWrapper'
import AddBlockBuilder from '@/components/optypes/frontend/AddBlockBuilder'

// components
import CopyHeader from '@/components/optypes/frontend/SideScroller/CopyHeader'
import BlocksHeader from '@/components/optypes/frontend/SideScroller/BlocksHeader'

import ReactPlayer from 'react-player'
// import { Scrollbars } from 'react-custom-scrollbars'

// blocks
import Header from '@/components/optypes/frontend/SideScroller/blocks/Header'
import InputBlock from '@/components/optypes/frontend/SideScroller/blocks/InputBlock'
import ButtonBlock from '@/components/optypes/frontend/SideScroller/blocks/ButtonBlock'
import MultiChoiceBlock from '@/components/optypes/frontend/SideScroller/blocks/MultiChoiceBlock'
import LinkBlock from '@/components/optypes/frontend/SideScroller/blocks/LinkBlock'

import dynamic from 'next/dynamic'

// drag and drop context wrapper
import DragWrapper from '@/components/optypes/headlesss-components/DragWrapper'
import OnepagerFooter from '@/components/optypes/headlesss-components/OnepagerFooter'

const CardsBlock = dynamic(
  () => import('@/components/optypes/frontend/SideScroller/blocks/CardsBlock'),
  {
    ssr: false,
  }
)

const TodosBlock = dynamic(
  () => import('@/components/optypes/frontend/SideScroller/blocks/TodosBlock'),
  {
    ssr: false,
  }
)

const Scrollbars = dynamic(() => import('react-custom-scrollbars'), {
  ssr: false,
})

const Components = {
  header: Header,
  inputblock: InputBlock,
  cardblock: CardsBlock,
  todosblock: TodosBlock,
  ctablock: ButtonBlock,
  multichoiceblock: MultiChoiceBlock,
  linkblock: LinkBlock,
}

const SideScroller = () => {
  const [builder] = useAtom(isBuilder)
  // manifest settings
  const [opStatic] = useAtom(manifestStatic)
  const [opDesign] = useAtom(manifestDesign)
  const [opBlocks] = useAtom(manifestBlocks)
  // custom page settings
  const [pageStatic] = useAtom(pageContentStatic)
  const [pageDesign] = useAtom(pageContentDesign)
  const [pageBlocks] = useAtom(pageContentBlocks)

  const [fullScreen, setFullScreen] = useState(false)
  const [getHeaderSize, setHeaderSize] = useAtom(headerSize)

  return (
    <div
      className=" md:flex md:overflow-hidden opr-sidebar-parent"
      style={{
        // height: builder ? 'calc(100vh - 4rem)' : '100vh',
        height: 'calc(100vh - ' + getHeaderSize + 'px)',
      }}
    >
      {/* copy side */}
      <div
        className={Classnames(
          'md:h-full opr-sidebar  text-white shadow-xl border-r border-black border-opacity-25 relative z-20 pb-24',
          {
            '': !fullScreen,
            hidden: fullScreen,
            'md:w-1/2':
              returnValue('widthstyle', opDesign, pageDesign) == '50%',
            'md:w-1/3':
              returnValue('widthstyle', opDesign, pageDesign) == '33%',
            'md:w-1/4':
              returnValue('widthstyle', opDesign, pageDesign) == '25%',
          }
        )}
        style={{
          backgroundColor: returnValue('bgcolor', opDesign, pageDesign),
          color: returnValue('maintextcolor', opDesign, pageDesign),
          boxShadow:
            '10px 0 11px 4px rgba(0,0,0,0.08), 5px 0 7px 4px rgba(0,0,0,0.1)',
        }}
      >
        {/* background image */}
        {returnValue('bgimgurl', opDesign, pageDesign) && (
          <div
            className="absolute top-0 left-0 z-0 h-full w-full  bg-cover bg-no-repeat"
            style={{
              backgroundImage: `url(${returnValue(
                'bgimgurl',
                opDesign,
                pageDesign
              )})`,
              opacity: returnValue('bgimgopacity', opDesign, pageDesign),
            }}
          ></div>
        )}

        <CopyHeader />

        <Scrollbars
          style={{
            width: '100%',
            height: '100%',
            // height: builder ? 'calc(100vh - 8rem)' : 'calc(100vh - 4rem)',
            // height: 'calc(100vh - ' + getHeaderSize + 'px)',
          }}
          className="relative z-20"
        >
          <div className="p-12 pb-20 relative z-20">
            {/* video */}
            {returnValue('videourl', opStatic, pageStatic) && (
              <div className="opr-header-video player-wrapper rounded shadow-lg overflow-hidden mb-8">
                <ReactPlayer
                  controls={returnValue('videocontrols', opStatic, pageStatic)}
                  playing={false}
                  width="100%"
                  height="100%"
                  className="react-player"
                  url={returnValue('videourl', opStatic, pageStatic)}
                />
              </div>
            )}

            {/* img */}
            {returnValue('imgurl', opStatic, pageStatic) && (
              <img
                src={returnValue('imgurl', opStatic, pageStatic)}
                alt=""
                className="opr-header-image rounded shadow-lg mb-8"
              />
            )}

            {/* headline / subheadline */}
            <div className="opr-sidebar-header-text">
              <h1
                className={Classnames('font-bold text-6xl', {
                  appTitleItem:
                    returnValue('herofont', opDesign, pageDesign) == 'sans',
                })}
                style={{
                  color: returnValue('headlinetextcolor', opDesign, pageDesign),
                }}
              >
                <StaticContentWrapper contentKey="headline">
                  {returnValue('headline', opStatic, pageStatic)}
                </StaticContentWrapper>
              </h1>
              <p className="mt-6 text-xl font-medium">
                <StaticContentWrapper contentKey="subheadline">
                  {returnValue('subheadline', opStatic, pageStatic)}
                </StaticContentWrapper>
              </p>
            </div>

            {/* divider */}
            <div
              className="opr-sidebar-divider w-1/4 h-1 rounded opacity-20 my-8"
              style={{
                backgroundColor: returnValue(
                  'copydivider',
                  opDesign,
                  pageDesign
                ),
              }}
            ></div>

            {/* long form copy */}
            <StaticContentWrapper contentKey="maincopy">
              <div
                className="prose opr-maincopy"
                dangerouslySetInnerHTML={{
                  __html: returnValue('maincopy', opStatic, pageStatic),
                }}
                style={{
                  color: returnValue('maintextcolor', opDesign, pageDesign),
                }}
              >
                {/* {returnValue('maincopy', opStatic, pageStatic)} */}
              </div>
            </StaticContentWrapper>
          </div>
          {!builder && (
            <div
              className="hidden md:block md:fixed bottom-2.5 left-2.5 z-40"
              style={{
                color: returnValue('maintextcolor', opDesign, pageDesign),
              }}
            >
              <OnepagerFooter />
            </div>
          )}
        </Scrollbars>
      </div>

      {/* side scroller content blocks */}
      <div
        className={Classnames(
          'opr-main-area md:h-full overflow-y-auto shadow-inner',
          {
            // 'md:w-1/2': !fullScreen,
            // 'md:w-full': fullScreen,
            'md:w-1/2':
              returnValue('widthstyle', opDesign, pageDesign) == '50%',
            'md:w-2/3':
              returnValue('widthstyle', opDesign, pageDesign) == '33%',
            'md:w-3/4':
              returnValue('widthstyle', opDesign, pageDesign) == '25%',
          }
        )}
        style={{
          backgroundColor: returnValue('blockbgcolor', opDesign, pageDesign),
          color: returnValue('blocktextcolor', opDesign, pageDesign),
        }}
      >
        <BlocksHeader />

        <div className="p-12 space-y-4">
          <DragWrapper>
            {pageBlocks.map((block, index) => (
              <BlockRenderer
                key={index}
                index={index}
                Components={Components}
                block={block}
              />
            ))}
          </DragWrapper>

          {/* add block button builder */}
          {builder && <AddBlockBuilder btnText="Add Block" />}
        </div>
      </div>
    </div>
  )
}

export default SideScroller
